// Fixed-fare pricing model, vehicle classes, child seats and services.
// NOTE: prices are example/MOCK values (CHF) and can be replaced with a real tariff.

export const VEHICLES = [
  {
    id: "economy",
    name: "Economy Klasse",
    model: "Škoda Octavia o. ä.",
    seats: 3,
    luggage: 3,
    base: 40, // base fare CHF
    perKm: 2.6, // CHF per km
    img: "https://customer-assets.emergentagent.com/job_zurich-airport-cab/artifacts/x5ooji4t_IMG-20260704-WA0000.jpg",
  },
  {
    id: "business",
    name: "Business Class",
    model: "Mercedes E-Klasse / Audi A6 / BMW 5er",
    seats: 3,
    luggage: 3,
    base: 60,
    perKm: 3.4,
    img: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/70c63dbf3a7a830cd4e30f3f5263d0e0192940397ace6a8a655c7bd970e8eedb.png",
  },
  {
    id: "first",
    name: "First Class",
    model: "Mercedes-Maybach / S-Klasse",
    seats: 3,
    luggage: 3,
    base: 90,
    perKm: 4.2,
    img: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/4a6f1bb5b20a7333c87fce7bbb7941d16e11e75aab59f90160f036b9295b132f.png",
  },
  {
    id: "van",
    name: "Standard XXL Van",
    model: "Mercedes V-Klasse XXL",
    seats: 7,
    luggage: 7,
    base: 70,
    perKm: 3.6,
    img: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/0bf1e9dc45a1894080e939c366608a58879df43c993c7fc5440be65fc5e24a63.png",
  },
  {
    id: "sprinter",
    name: "Sprinter",
    model: "Mercedes Sprinter",
    seats: 14,
    luggage: 14,
    base: 110,
    perKm: 4.0,
    img: "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/105ea2e1c70d04052a481ccadc7a6b88c282dc11a2160bb6240fd615917c0ae0.png",
  },
];

export const getVehicle = (id) => VEHICLES.find((v) => v.id === id) || VEHICLES[0];

// ---- km-tier pricing ----
const TIER_BOUNDS = [5, 10, 15, 20, 25, 30, 35, 40, 50, 55, 60, 65, 70, 75, 80, 85, 90, 100];
const genTiers = (base, perKm) => TIER_BOUNDS.map((upto) => ({ upto, price: Math.max(base, Math.round((perKm * upto) / 5) * 5) }));
VEHICLES.forEach((v) => { v.tiers = genTiers(v.base, v.perKm); v.perKmOver = v.perKm; });

// ---- night surcharge (admin-managed) ----
let FEES = { nightPercent: 15, nightStart: 22, nightEnd: 6 };
export const applyFees = (f) => { if (f && typeof f === "object") FEES = { ...FEES, ...f }; };
export const getFees = () => ({ ...FEES });
export const isNight = (time) => {
  if (!time) return false;
  const h = parseInt(String(time).split(":")[0], 10);
  if (isNaN(h)) return false;
  const { nightStart, nightEnd } = FEES;
  return nightStart > nightEnd ? (h >= nightStart || h < nightEnd) : (h >= nightStart && h < nightEnd);
};
export const applyNight = (price, time) =>
  isNight(time) ? Math.round((price * (1 + FEES.nightPercent / 100)) / 5) * 5 : price;

// Apply admin-managed pricing (from backend) onto the in-memory vehicle list.
export const applyPricing = (list) => {
  if (!Array.isArray(list)) return;
  list.forEach((p) => {
    const v = VEHICLES.find((x) => x.id === p.id);
    if (!v) return;
    if (typeof p.base === "number") v.base = p.base;
    if (typeof p.perKm === "number") v.perKm = p.perKm;
    if (typeof p.perKmOver === "number") v.perKmOver = p.perKmOver;
    if (typeof p.name === "string" && p.name) v.name = p.name;
    if (typeof p.img === "string" && p.img) v.img = p.img;
    if (Array.isArray(p.tiers) && p.tiers.length) v.tiers = p.tiers;
  });
};

// Apply admin-managed vehicle images.
export const applyVehicleImages = (imgs) => {
  if (!imgs || typeof imgs !== "object") return;
  Object.entries(imgs).forEach(([id, url]) => {
    if (!url) return;
    const v = VEHICLES.find((x) => x.id === id);
    if (v) v.img = url;
  });
};

// Fixed fare using km-tiers. Each tier is the full price for a trip up to `upto` km.
// Beyond the last tier (100 km) a per-km rate applies. When `time` is a night hour,
// the admin-managed night surcharge is added.
export const computeFare = (vehicleId, km, time) => {
  const v = getVehicle(vehicleId);
  const d = km || 0;
  let price;
  if (Array.isArray(v.tiers) && v.tiers.length) {
    const tier = v.tiers.find((t) => d <= t.upto);
    if (tier) {
      price = tier.price;
    } else {
      const last = v.tiers[v.tiers.length - 1];
      const over = (v.perKmOver != null ? v.perKmOver : v.perKm) || 0;
      price = last.price + Math.round(((d - last.upto) * over) / 5) * 5;
    }
  } else {
    price = Math.max(v.base, Math.round((v.perKm * d) / 5) * 5);
  }
  return applyNight(price, time);
};

// Child seats (free of charge) — each has its own info blog page.
export const CHILD_SEATS = [
  {
    id: "maxicosi",
    name: "Maxi-Cosi",
    subtitle: "0–15 Monate · 0–13 kg",
    slug: "maxi-cosi",
    icon: "baby",
    img: "https://static.prod-images.emergentagent.com/jobs/2c3518a3-4c9c-454e-8698-21a3b571ac3c/images/872525225b0f9cb4d2fabbf20007a868e3fabc1debfb0172eecc6002b0036f87.jpeg",
    short:
      "Babyschale (Gruppe 0+) für Neugeborene und Säuglinge bis 13 kg — sicher rückwärts montiert.",
  },
  {
    id: "kindersitz",
    name: "Kindersitz",
    subtitle: "9 Monate–4 Jahre · 9–18 kg",
    slug: "kindersitz",
    icon: "car-seat",
    img: "https://images.unsplash.com/photo-1619719287848-883c8f26efbc",
    short:
      "Vollwertiger Kindersitz (Gruppe 1) mit Fangkörper/Gurtsystem für Kleinkinder von 9 bis 18 kg.",
  },
  {
    id: "sitzerhohung",
    name: "Sitzerhöhung",
    subtitle: "4–12 Jahre · 15–36 kg",
    slug: "sitzerhoehung",
    icon: "booster",
    img: "https://static.prod-images.emergentagent.com/jobs/2c3518a3-4c9c-454e-8698-21a3b571ac3c/images/0f610829a895523cb00dab4d3fd3367a7520e30faabc57fd85a3ea7ccc12e99d.jpeg",
    short:
      "Sitzerhöhung (Gruppe 2/3), damit der Sicherheitsgurt bei grösseren Kindern korrekt sitzt.",
  },
];

export const getChildSeatBySlug = (slug) =>
  CHILD_SEATS.find((c) => c.slug === slug);

// All services shown in the footer / services section of the homepage.
export const ALL_SERVICES = [
  { icon: "plane", title: "Flughafentransfer", desc: "Fixpreis-Transfers von und zum Flughafen Zürich (ZRH), rund um die Uhr.", link: "/flughafentransfer" },
  { icon: "route", title: "Stadt-zu-Stadt", desc: "Direkte Fahrten zwischen allen Schweizer Städten – bequem und ohne Umsteigen.", link: "/stadttransfer-zurich" },
  { icon: "mountain", title: "Ski- & Bergtransfers", desc: "Transfers nach Davos, St. Moritz, Zermatt, Verbier und in alle Skiorte.", link: "/ski-transfer-zurich" },
  { icon: "briefcase", title: "Business & Roadshows", desc: "Diskrete Chauffeure für Termine, Meetings und mehrtägige Roadshows.", link: "/business-transfer-zurich" },
  { icon: "clock", title: "Stunden- & Tagesmiete", desc: "Chauffeur auf Abruf – stundenweise oder für den ganzen Tag.", link: "/stundenservice-zurich" },
  { icon: "users", title: "Gruppen & Events", desc: "Vans und Sprinter für Hochzeiten, Konzerte, Messen und Ausflüge.", link: "/gruppentransfer-zurich" },
  { icon: "plane", title: "Kreuzfahrt-Transfers", desc: "Transfers zu Häfen und Anschlussflughäfen in der ganzen Region.", link: "/kreuzfahrt-transfer-zurich" },
  { icon: "shield", title: "Kindersichere Fahrten", desc: "Maxi-Cosi, Kindersitz und Sitzerhöhung – kostenlos auf Wunsch.", link: "/kindersichere-fahrten-zurich" },
];
